import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import logger from '../utils/secureLogger';

/**
 * Store para gestión del estado del chat HealthOps
 * Implementa principios SOLID: SRP - responsabilidad única para estado del chat
 * Utiliza Zustand con persistencia para mantener historial de conversación
 */
export const useHealthOpsChatStore = create(
  persist(
    (set, get) => ({
      // Estado del chat
      conversations: {},
      currentSessionId: null,
      preferences: {
        autoScroll: true,
        soundEnabled: false,
        theme: 'default'
      },

      // Acciones para gestión de conversaciones
      createSession: (sessionId = null) => {
        const id = sessionId || `session_${Date.now()}`;
        const newConversation = {
          id,
          messages: [{
            id: 1,
            type: "agent",
            content: "Hello! I'm **HealthOps**, your AWS Health Events specialist. \n\nHow can I assist you today?",
            timestamp: new Date()
          }],
          createdAt: new Date(),
          lastActivity: new Date()
        };

        set(state => ({
          conversations: {
            ...state.conversations,
            [id]: newConversation
          },
          currentSessionId: id
        }));

        logger.debug('🆕 New HealthOps chat session created:', id);
        return id;
      },

      setCurrentSession: (sessionId) => {
        const { conversations } = get();
        if (conversations[sessionId]) {
          set({ currentSessionId: sessionId });
          logger.debug('🔄 Switched to HealthOps session:', sessionId);
        } else {
          logger.warn('⚠️ Session not found:', sessionId);
        }
      },

      addMessage: (sessionId, message) => {
        set(state => {
          const conversation = state.conversations[sessionId];
          if (!conversation) return state;

          const updatedConversation = {
            ...conversation,
            messages: [...conversation.messages, message],
            lastActivity: new Date()
          };

          return {
            conversations: {
              ...state.conversations,
              [sessionId]: updatedConversation
            }
          };
        });
      },

      clearSession: (sessionId) => {
        set(state => {
          const newConversations = { ...state.conversations };
          delete newConversations[sessionId];
          
          return {
            conversations: newConversations,
            currentSessionId: state.currentSessionId === sessionId ? null : state.currentSessionId
          };
        });
        
        logger.debug('🗑️ HealthOps session cleared:', sessionId);
      },

      clearAllSessions: () => {
        set({
          conversations: {},
          currentSessionId: null
        });
        logger.debug('🧹 All HealthOps sessions cleared');
      },

      // Getters
      getCurrentConversation: () => {
        const { conversations, currentSessionId } = get();
        return currentSessionId ? conversations[currentSessionId] : null;
      },

      getSessionList: () => {
        const { conversations } = get();
        return Object.values(conversations)
          .sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity));
      },

      // Preferencias
      updatePreferences: (newPreferences) => {
        set(state => ({
          preferences: {
            ...state.preferences,
            ...newPreferences
          }
        }));
      },

      // Utilidades
      getMessageCount: (sessionId) => {
        const { conversations } = get();
        return conversations[sessionId]?.messages.length || 0;
      },

      exportConversation: (sessionId) => {
        const { conversations } = get();
        const conversation = conversations[sessionId];
        if (!conversation) return null;

        return {
          ...conversation,
          exportedAt: new Date()
        };
      }
    }),
    {
      name: 'healthops-chat-storage',
      partialize: (state) => ({
        conversations: state.conversations,
        currentSessionId: state.currentSessionId,
        preferences: state.preferences
      }),
      // Deserializar fechas al recuperar del localStorage
      onRehydrateStorage: () => (state) => {
        if (state?.conversations) {
          Object.keys(state.conversations).forEach(sessionId => {
            const conversation = state.conversations[sessionId];
            
            // Convertir fechas de la conversación
            if (conversation.createdAt && typeof conversation.createdAt === 'string') {
              conversation.createdAt = new Date(conversation.createdAt);
            }
            if (conversation.lastActivity && typeof conversation.lastActivity === 'string') {
              conversation.lastActivity = new Date(conversation.lastActivity);
            }
            
            // Convertir fechas de los mensajes
            if (conversation.messages) {
              conversation.messages.forEach(message => {
                if (message.timestamp && typeof message.timestamp === 'string') {
                  message.timestamp = new Date(message.timestamp);
                }
              });
            }
          });
        }
      }
    }
  )
);